import { supabase } from '../../client';
import { logError } from '@/lib/sentry';
import type {
  MessageTemplate,
  CreateMessageTemplateInput,
  UpdateMessageTemplateInput,
} from '@/types/messageTemplate';

const TABLE_NAME = 'message_template';

function toMessageTemplate(row: {
  id: number;
  name: string;
  content: string;
  sort_order: number;
  created_at: string;
  modified_at: string;
}): MessageTemplate {
  return {
    id: row.id,
    name: row.name,
    content: row.content,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    modifiedAt: row.modified_at,
  };
}

const SELECT_COLUMNS = 'id, name, content, sort_order, created_at, modified_at';

export async function getMessageTemplates(userId: string): Promise<MessageTemplate[]> {
  if (!userId) return [];

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select(SELECT_COLUMNS)
    .eq('user_id', userId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    logError('Failed to fetch message templates', error);
    throw error;
  }

  return (data ?? []).map(toMessageTemplate);
}

export async function createMessageTemplate(
  userId: string,
  input: CreateMessageTemplateInput,
): Promise<MessageTemplate> {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .insert({
      user_id: userId,
      name: input.name,
      content: input.content,
      sort_order: input.sortOrder ?? 0,
    })
    .select(SELECT_COLUMNS)
    .single();

  if (error) {
    logError('Failed to create message template', error);
    throw error;
  }

  return toMessageTemplate(data);
}

export async function updateMessageTemplate(
  userId: string,
  templateId: number,
  updates: UpdateMessageTemplateInput,
): Promise<MessageTemplate> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) payload.name = updates.name;
  if (updates.content !== undefined) payload.content = updates.content;
  if (updates.sortOrder !== undefined) payload.sort_order = updates.sortOrder;

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .update(payload)
    .eq('user_id', userId)
    .eq('id', templateId)
    .select(SELECT_COLUMNS)
    .single();

  if (error) {
    logError('Failed to update message template', error);
    throw error;
  }

  return toMessageTemplate(data);
}

export async function deleteMessageTemplate(userId: string, templateId: number): Promise<void> {
  if (!userId) return;

  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq('user_id', userId)
    .eq('id', templateId);

  if (error) {
    logError('Failed to delete message template', error);
    throw error;
  }
}
